import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * @extends {Component}
 */
class WceCartRecommendationsLoader extends Component {
  #observer = null;
  #abortController = null;
  /** @type {Element | null} */
  #drawer = null;
  #onDrawerOpen = () => {
    requestAnimationFrame(() => this.#loadIfVisible());
  };

  connectedCallback() {
    super.connectedCallback();

    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.#observe();
    requestAnimationFrame(() => this.#loadIfVisible());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.#drawer = null;
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  handleCartUpdate(event) {
    event.promise
      ?.then(() => this.#resolveProductId())
      .then((productId) => {
        if (!this.isConnected) return;

        if (!productId) {
          this.#clear();
          return;
        }

        this.dataset.productId = productId;
        delete this.dataset.loaded;
        this.#observer?.disconnect();
        this.#load();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error(error);
      });
  }

  /**
   * Reads the current cart directly (rather than guessing at the shape of
   * CartLinesUpdateEvent's resolved payload) to find the new seed product.
   */
  async #resolveProductId() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();

    return cart.items?.length ? String(cart.items[0].product_id) : '';
  }

  #clear() {
    this.#abortController?.abort();
    this.innerHTML = '';
    delete this.dataset.loaded;
    delete this.dataset.productId;
  }

  #observe() {
    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;

        this.#observer?.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 200px 0px' },
    );

    this.#observer.observe(this);
  }

  #loadIfVisible() {
    if (this.dataset.loaded === 'true' && this.innerHTML.trim().length) return;
    if (!this.dataset.productId) return;

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (rect.top <= viewportHeight + 200 && rect.bottom >= 0) {
      this.#observer?.disconnect();
      this.#load();
    }
  }

  #load() {
    const productId = this.dataset.productId;
    if (!productId) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const url = `${this.dataset.url}&product_id=${productId}`;

    fetch(url, { signal })
      .then((response) => response.text())
      .then((text) => {
        if (signal.aborted) return;

        const html = document.createElement('div');
        html.innerHTML = text;

        html.querySelectorAll('script').forEach((oldScript) => {
          if (oldScript.src || !oldScript.textContent.trim()) return;

          const script = document.createElement('script');
          if (oldScript.type) script.type = oldScript.type;
          script.textContent = oldScript.textContent;
          document.head.appendChild(script);
          script.remove();
        });

        const section = html.querySelector('#shopify-section-wce-cart-recommendations');

        if (section && section.innerHTML.trim().length) {
          this.innerHTML = section.innerHTML;
          this.dataset.loaded = 'true';
        } else {
          this.innerHTML = '';
          delete this.dataset.loaded;
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  document.querySelectorAll('wce-cart-recommendations-loader').forEach((element) => {
    element.handleCartUpdate?.(event);
  });
});

if (!customElements.get('wce-cart-recommendations-loader')) {
  customElements.define('wce-cart-recommendations-loader', WceCartRecommendationsLoader);
}
